import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { motion, AnimatePresence } from "framer-motion";
// import { useAuth } from "@/contexts/AuthContext";
import { useDispatch } from "react-redux";
import { addSellerLead, signup } from "../store/auth/authActions";
import { Eye, EyeOff } from "lucide-react";

const RegistrationForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  // const { signup } = useAuth();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: "",
    companyName: "",
    email: "",
    phone: "",
    postalCode: "",
    password: "",
    confirmPassword: ""
  });
  const [showPasswords, setShowPasswords] = useState({
    password: false,
    confirmPassword: false
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = useCallback((e) => {
    const { id, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [id]: value
    }));
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (formData.password !== formData.confirmPassword) {
        toast({
          title: "Passwords do not match",
          description: "Please check your password and try again.",
          variant: "destructive"
        });
        return;
      }
      setIsLoading(true);

      try {
        // This form now creates a real seller account
        const signupData = {
          email: formData.email,
          password: formData.password,
          role: "seller",
          name: formData.name,
          companyName: formData.companyName
        };

        await dispatch(
          signup({
            signupData: signupData,
            callback: () => {}
          })
        ).unwrap();

        // Also save lead info for marketing/sales purposes
        await dispatch(
          addSellerLead({
            name: formData.name,
            companyName: formData.companyName,
            email: formData.email,
            phone: formData.phone,
            postalCode: formData.postalCode
          })
        ).unwrap();
      } catch (error) {
        // The signup function in AuthContext will show a toast on error
        console.error("Error during seller registration:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [formData, navigate, toast, signup]
  );

  const handleShowPassword = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const getPasswordValidation = () => {
    const password = formData.password;

    if (!password) return "";

    if (password.length < 6)
      return "Password must be at least 6 characters long.";
    if (password.length > 16) return "Password cannot exceed 16 characters.";
    if (!/[A-Z]/.test(password))
      return "Password must contain at least one uppercase letter.";
    if (!/[a-z]/.test(password))
      return "Password must contain at least one lowercase letter.";
    if (!/[0-9]/.test(password))
      return "Password must include at least one number.";
    if (!/[!@#$%^&*(),.?\":{}|<>]/.test(password))
      return "Password must include at least one special character.";

    return "";
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Full Name (Contact)</Label>
          <Input
            id="name"
            type="text"
            placeholder="John Doe"
            value={formData.name}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="companyName">Company Name</Label>
          <Input
            id="companyName"
            type="text"
            placeholder="Auto Parts Inc."
            value={formData.companyName}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="email">Email Address</Label>
        <Input
          id="email"
          type="email"
          placeholder="john.doe@example.com"
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete
          disabled={isLoading}
          pattern="^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
          title="Enter a valid email address (e.g., name@example.com)"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            type="tel"
            placeholder="(123) 456-7890"
            value={formData.phone}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="postalCode">Postal Code</Label>
          <Input
            id="postalCode"
            type="text"
            placeholder="A1B 2C3"
            value={formData.postalCode}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPasswords?.password ? "text" : "password"}
              value={formData.password}
              onChange={handleChange}
              autoComplete="new-password"
              required
              disabled={isLoading}
              maxLength={16}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => handleShowPassword("password")}
                disabled={isLoading}
              >
                {showPasswords?.password ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>
          <AnimatePresence>
            {getPasswordValidation() && (
              <motion.p
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="text-xs text-red-400"
              >
                {getPasswordValidation()}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <div className="relative">
            <Input
              id="confirmPassword"
              type={showPasswords?.confirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                onClick={() => handleShowPassword("confirmPassword")}
                disabled={isLoading}
              >
                {showPasswords?.confirmPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>
          <AnimatePresence>
            {formData.confirmPassword &&
              formData.password !== formData.confirmPassword && (
                <motion.p
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="text-xs text-red-400"
                >
                  {formData.confirmPassword &&
                    password !== formData.confirmPassword &&
                    "Passwords do not match"}
                </motion.p>
              )}
          </AnimatePresence>
        </div>
      </div>
      <Button
        type="submit"
        className="w-full h-auto px-2 xs:px-auto"
        disabled={isLoading}
      >
        {isLoading ? "Creating Account..." : "Create Account & Choose Plan"}
      </Button>
    </form>
  );
};

export default RegistrationForm;
